import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { hasPermission } from '../../utils/permissionUtils';
import { useLocation } from "react-router-dom";
import DataTable from 'react-data-table-component';
import {
  Box,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  Paper,
  Divider,
  Drawer,
  IconButton,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

const ExpandedComponent = ({ data }) => {
  const theme = useTheme();
  return (
    <Box p={2} sx={{ backgroundColor: theme.palette.action.hover }}>
      <Typography variant="h6" sx={{ mb: 1 }} color="textPrimary">
        Exception Log Details for {data.error_code}
      </Typography>
      <Typography variant="body2">
        <strong>Exception Log Message:</strong> {data.message}
      </Typography>
    </Box>
  );
};

export default function ListExceptionLog() {
  const theme = useTheme();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fetchedUrl = queryParams.get('fetched_url');

  const [exceptionLogs, setExceptionLogs] = useState([]);
  const [filteredExceptionLogs, setFilteredExceptionLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [reload, setReload] = useState(false);

  // Permission states
  const [canView, setCanView] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission('chat-history', 'view');
        setCanView(viewPerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setCanView(false);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, []);

  // Function to toggle the reload state
  const handleReload = () => {
    setReload(!reload);
  };

  useEffect(() => {
    document.title = "Exception Logs | KlonIT Web Application";
    if (canView && !checkingPermissions) {
      fetchExceptionLogs();
    }
  }, [reload, canView, checkingPermissions]);

  // Function to fetch exceptionLogs from the API
  const fetchExceptionLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/get-exception-log`);
      const responseData = typeof response.data.data === 'string' 
        ? JSON.parse(response.data.data) 
        : response.data.data;

      setExceptionLogs(responseData);
      setFilteredExceptionLogs(responseData);
      setError(null);
    } catch (error) {
      console.error("Error fetching exception logs:", error);
      setError(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = exceptionLogs.filter(
      (doc) =>
        (doc.error_code?.toString() ?? '').includes(value) ||
        doc.log_type.toLowerCase().includes(value) ||
        doc.status.toLowerCase().includes(value) ||
        doc.created_on.toLowerCase().includes(value) ||
        doc.updated_on.toLowerCase().includes(value)
    );
    setFilteredExceptionLogs(filtered);
  };

  const columns = [
    {
      name: "Reference ID",
      selector: (row) => row.error_code,
      sortable: true,
    },
    {
      name: "Log Type",
      selector: (row) => row.log_type,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
    },
    {
      name: "Created On",
      selector: (row) => row.created_on,
      sortable: true,
    },
    {
      name: "Updated On",
      selector: (row) => row.updated_on,
      sortable: true,
    }
  ];

  const customDataTableStyles = {
    table: {
      style: {
        backgroundColor: theme.palette.background.paper,
      },
    },
    headRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"
        }`,
      },
    },
    headCells: {
      style: {
        fontWeight: 600,
        fontSize: "14px",
        color: theme.palette.text.primary,
      },
    },
    rows: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
        },
      },
    },
    pagination: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderTop: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
        }`,
      },
    },
    expanderRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb",
      },
    },
  };

  const toggleDrawer = (open) => {
    setIsDrawerOpen(open);
  };

  // Show loading state while checking permissions
  if (checkingPermissions) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress size={30} />
        <Typography ml={2}>Checking permissions...</Typography>
      </Box>
    );
  }

  // Show access denied if no view permission
  if (!canView) {
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <Alert severity="error">
          <strong>Access Denied</strong>
          <br />
          You don't have permission to view logging. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 10, mx: 'auto', maxWidth: '1200px', bgcolor: 'background.paper' }}>
      <Typography variant="h5" component="h1" sx={{ mb: 3 }} color="textPrimary">
        Exception <strong>Logs</strong>
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" color="text.primary">
          Monitor and track system exceptions
        </Typography>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search logs..."
          value={searchTerm}
          onChange={handleSearch}
          sx={{ minWidth: '250px' }}
        />
      </Box>

      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={4}>
          <CircularProgress size={30} />
          <Typography mt={2}>Loading exception logs...</Typography>
        </Box>
      ) : filteredExceptionLogs.length === 0 ? (
        <Box textAlign="center" py={4}>
          <i className="fa fa-exclamation-triangle fa-3x text-muted" />
          <Typography mt={2} color="text.primary">
            No exception logs available
          </Typography>
        </Box>
      ) : (
        <DataTable
          columns={columns}
          data={filteredExceptionLogs}
          pagination
          responsive
          highlightOnHover
          pointerOnHover
          expandableRows
          expandableRowsComponent={ExpandedComponent}
          customStyles={customDataTableStyles}
          theme={theme.palette.mode === "dark" ? "dark" : "default"}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <i className="fa fa-exclamation-triangle" /> {error}
        </Alert>
      )}

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => toggleDrawer(false)}
        PaperProps={{ style: { width: "500px" } }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px' }}>
          <IconButton onClick={() => toggleDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </div>
      </Drawer>
    </Paper>
  );
}